import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDropzone } from "react-dropzone";
import { useSearchParams } from "react-router-dom";
import { useIntl } from "react-intl";
import DashboardClientLayout from "../../Layouts/DashboardClientLayout";
import "./HomePage.css";
import profileAvatar from "../../assets/images/avatar.png";
import loyaltyBadge from "../../assets/images/badge.png";
import countryCodeData from "../Modals/Accountmanager/Update/data/countryCode/index.json";
import { showSnackbar } from "../../Redux/SnackBar/actions";
import profileApi from "../../Services/Profile";
import driverApi from "../../Services/Driver";
import { CUSTOM_CONSTANTS, apiUrl } from "../../Constants";

function getEmptyValue(intl) {
  return intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.common.notProvided" });
}

function getPaymentMethodOptions(intl) {
  return [
    { value: "CASH", label: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.paymentMethod.cash" }) },
    { value: "CARD", label: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.paymentMethod.card" }) },
    { value: "WALLET", label: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.paymentMethod.wallet" }) },
    { value: "MOBILE_MONEY", label: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.paymentMethod.mobileMoney" }) },
  ];
}

function getAccountStatusOptions(intl) {
  return [
    { value: "ACTIVE", label: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.accountStatus.active" }) },
    { value: "INACTIVE", label: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.accountStatus.inactive" }) },
    { value: "SUSPENDED", label: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.accountStatus.suspended" }) },
    { value: "BLOCKED", label: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.accountStatus.blocked" }) },
  ];
}

function getIdentityTypeOptions(intl) {
  return [
    { value: "NATIONAL_ID", label: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityType.nationalId" }) },
    { value: "PASSPORT", label: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityType.passport" }) },
    { value: "DRIVING_LICENSE", label: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityType.drivingLicense" }) },
  ];
}

// Les noms de pays viennent du fichier de données countryCode (déjà localisé côté data), pas de clé i18n ici.
const countryCodeOptions = countryCodeData.map((country) => ({
  value: country.dial_code,
  label: `${country.name} (${country.dial_code})`,
}));

const maxIdentityFileSize = 5 * 1024 * 1024;

function cleanText(value) {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value).trim();
}

function firstText(...values) {
  return values.map(cleanText).find(Boolean) || "";
}

function displayValue(intl, value, fallback) {
  return firstText(value) || fallback || getEmptyValue(intl);
}

function buildFullName(user = {}) {
  return firstText(
    user.name,
    [firstText(user.first_name, user.firstName), firstText(user.last_name, user.lastName)]
      .filter(Boolean)
      .join(" ")
  );
}

function formatDate(intl, value) {
  if (!value) {
    return getEmptyValue(intl);
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return displayValue(intl, value);
  }

  return new Intl.DateTimeFormat(intl.locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function formatDateTime(intl, value) {
  if (!value) {
    return getEmptyValue(intl);
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return displayValue(intl, value);
  }

  return new Intl.DateTimeFormat(intl.locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatAmount(intl, value) {
  const amount = Number(value);
  const currency = CUSTOM_CONSTANTS.BASE_CURRENCY;

  if (Number.isFinite(amount)) {
    return `${new Intl.NumberFormat(intl.locale, {
      maximumFractionDigits: 2,
    }).format(amount)} ${currency}`;
  }

  return value ? `${value} ${currency}` : `0 ${currency}`;
}

function formatInteger(intl, value) {
  const count = Number(value);

  if (Number.isFinite(count)) {
    return new Intl.NumberFormat(intl.locale, {
      maximumFractionDigits: 0,
    }).format(count);
  }

  return "0";
}

function formatPoints(intl, value) {
  return intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.common.pointsValue" }, { points: formatInteger(intl, value) });
}

function formatRating(intl, value) {
  const rating = Number(value);

  if (Number.isFinite(rating)) {
    return `${new Intl.NumberFormat(intl.locale, {
      maximumFractionDigits: 1,
    }).format(rating)}/5`;
  }

  return "0/5";
}

function formatPhone(intl, user = {}) {
  const phone = firstText(user.phone, user.mobile_number, user.mobileNumber);
  if (!phone) {
    return getEmptyValue(intl);
  }

  if (phone.startsWith("+")) {
    return phone;
  }

  return firstText(user.country_code, user.countryCode)
    ? `${firstText(user.country_code, user.countryCode)} ${phone}`
    : phone;
}

function formatPaymentMethod(intl, value) {
  const methods = {
    CASH: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.paymentMethod.cash" }),
    CARD: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.paymentMethod.card" }),
    MOBILE_MONEY: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.paymentMethod.mobileMoney" }),
    WALLET: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.paymentMethod.wallet" }),
    STRIPE: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.paymentMethod.card" }),
  };

  return methods[value] || displayValue(intl, value);
}

function formatStatus(intl, value) {
  const statuses = {
    ACTIVE: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.accountStatus.active" }),
    INACTIVE: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.accountStatus.inactive" }),
    BLOCKED: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.accountStatus.blocked" }),
    SUSPENDED: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.accountStatus.suspended" }),
  };

  return statuses[value] || displayValue(intl, value);
}

// Ces valeurs comparent des données venant de l'API (pas du texte affiché) : on ne les traduit pas,
// tout comme les codes CANCELLED/COMPLETED dans les autres pages.
function normalizeBooleanFlag(value) {
  if (typeof value === "string") {
    const normalizedValue = value.trim().toLowerCase();

    if (!normalizedValue) {
      return false;
    }

    if (
      [
        "false",
        "0",
        "no",
        "non",
        "n",
        "off",
        "inactive",
        "inactif",
        "pending",
        "unverified",
        "not verified",
        "not_verified",
      ].includes(normalizedValue)
    ) {
      return false;
    }

    if (
      [
        "true",
        "1",
        "yes",
        "oui",
        "y",
        "on",
        "active",
        "actif",
        "verified",
        "verifie",
        "vérifié",
        "validated",
        "valide",
        "valid",
      ].includes(normalizedValue)
    ) {
      return true;
    }
  }

  return Boolean(value);
}

function maskPhoneNumber(intl, phone, countryCode) {
  const phoneText = cleanText(phone);
  if (!phoneText) {
    return getEmptyValue(intl);
  }

  const countryText = cleanText(countryCode);
  const phoneDigits = phoneText.replace(/\D/g, "");
  const countryDigits = countryText.replace(/\D/g, "");
  const localDigits =
    countryDigits && phoneDigits.startsWith(countryDigits)
      ? phoneDigits.slice(countryDigits.length)
      : phoneDigits;

  if (localDigits.length < 4) {
    return phoneText;
  }

  const visibleStart = Math.min(2, Math.max(1, Math.ceil(localDigits.length / 4)));
  const visibleEnd = Math.min(3, Math.max(2, Math.ceil(localDigits.length / 4)));
  const maskedMiddle = "X".repeat(Math.max(localDigits.length - visibleStart - visibleEnd, 0));
  const pieces = [localDigits.slice(0, visibleStart), maskedMiddle, localDigits.slice(-visibleEnd)].filter(Boolean);
  const prefix = countryDigits ? `+${countryDigits}` : countryText.startsWith("+") ? countryText : "";

  return [prefix, pieces.join(" ")].filter(Boolean).join(" ").trim();
}

function formatCountdown(seconds) {
  const totalSeconds = Math.max(Number(seconds) || 0, 0);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;

  return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
}

function formatFileSize(intl, value) {
  const size = Number(value);

  if (!Number.isFinite(size) || size < 0) {
    return getEmptyValue(intl);
  }

  if (size < 1024) {
    return intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fileSize.bytes" }, { size });
  }

  const unitIds = [
    "dashboardEntreprise.profileEntreprise.fileSize.kb",
    "dashboardEntreprise.profileEntreprise.fileSize.mb",
    "dashboardEntreprise.profileEntreprise.fileSize.gb",
    "dashboardEntreprise.profileEntreprise.fileSize.tb",
  ];
  let unitIndex = -1;
  let displaySize = size;

  do {
    displaySize /= 1024;
    unitIndex += 1;
  } while (displaySize >= 1024 && unitIndex < unitIds.length - 1);

  const formatter = new Intl.NumberFormat(intl.locale, {
    maximumFractionDigits: displaySize < 10 ? 1 : 0,
  });

  return intl.formatMessage({ id: unitIds[unitIndex] }, { size: formatter.format(displaySize) });
}

function formatIdentityType(intl, value) {
  const types = {
    NATIONAL_ID: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityType.nationalId" }),
    PASSPORT: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityType.passport" }),
    DRIVING_LICENSE: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityType.drivingLicense" }),
  };

  return types[value] || displayValue(intl, value, intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityType.nationalId" }));
}

function formatIdentityFileType(intl, file) {
  const fileType = cleanText(file?.type).toLowerCase();

  if (!fileType) {
    return intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fileType.unknown" });
  }

  if (fileType.includes("pdf")) {
    return "PDF";
  }

  if (fileType.startsWith("image/")) {
    return intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fileType.image" });
  }

  return displayValue(intl, fileType.toUpperCase());
}

function normalizeIdentityType(value) {
  const text = cleanText(value).toUpperCase();

  if (!text) {
    return "NATIONAL_ID";
  }

  if (
    text === "NATIONAL_ID" ||
    text === "NATIONAL ID" ||
    text === "CNI" ||
    text === "CARTE NATIONALE D'IDENTITE" ||
    text === "CARTE NATIONALE D IDENTITE"
  ) {
    return "NATIONAL_ID";
  }

  if (text === "PASSPORT" || text === "PASSEPORT") {
    return "PASSPORT";
  }

  if (
    text === "DRIVING_LICENSE" ||
    text === "DRIVING LICENSE" ||
    text === "PERMIS DE CONDUIRE" ||
    text === "LICENSE"
  ) {
    return "DRIVING_LICENSE";
  }

  return text;
}

function maskIdentityNumber(value) {
  const text = cleanText(value);

  if (!text) {
    return "";
  }

  if (text.length <= 4) {
    return text;
  }

  return `${"*".repeat(Math.max(text.length - 4, 0))}${text.slice(-4)}`;
}

function pluralize(count, singular, plural) {
  return `${count} ${count > 1 ? plural : singular}`;
}
function normalizeUserProfile(intl, profileDetails, authUser) {
  const user = profileDetails || authUser || {};
  const savedAddresses = Array.isArray(user.user_fav_addresses)
    ? user.user_fav_addresses
    : [];
  const paymentCards = Array.isArray(user.payment_cards) ? user.payment_cards : [];
  const defaultAddress = savedAddresses.find((address) => address?.is_default) || savedAddresses[0] || {};
  const address = [
    firstText(user.address, defaultAddress.address),
    firstText(user.city),
    firstText(user.state),
    firstText(user.country),
  ]
    .filter(Boolean)
    .join(", ");
  const walletBalance = firstText(
    user.user_wallets?.wallet_balance,
    user.wallet_balance
  );
  const walletBalanceNumber = Number(walletBalance || 0);
  const idNumber = firstText(user.id_number, user.identity_number, user.identityNumber);
  const hasIdentityDocument = Boolean(
    cleanText(idNumber) ||
      cleanText(firstText(
        user.identity_document_name,
        user.identityDocumentName,
        user.identity_document,
        user.id_document,
        user.id_document_name
      )) ||
      normalizeBooleanFlag(user.identity_document_uploaded) ||
      normalizeBooleanFlag(user.identityDocumentUploaded) ||
      normalizeBooleanFlag(user.hasIdentityDocument)
  );
  const progressPercent = Number.isFinite(walletBalanceNumber)
    ? Math.min(Math.max((walletBalanceNumber / 2000) * 100, 0), 100)
    : 0;

  return {
    address: displayValue(intl, address),
    avatar: user.image_local !== "" ? apiUrl+user.image_local : profileAvatar,
    createdAt: formatDate(intl, user.created_at),
    displayName: firstText(buildFullName(user), buildFullName(authUser)) || intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.common.defaultUser" }),
    dueTrip: Number(user.due_trip || 0),
    email: displayValue(intl, firstText(user.email, authUser?.email)),
    emailVerified: normalizeBooleanFlag(user.email_verified),
    favAddressCount: savedAddresses.length,
    hasIdentityDocument,
    idVerified: normalizeBooleanFlag(user.id_verified),
    idNumber,
    idSubmittedAt: firstText(
      user.id_submitted_at,
      user.identity_submitted_at,
      user.id_uploaded_at,
      user.identity_uploaded_at
    ),
    idType: normalizeIdentityType(firstText(user.id_type, user.identity_type, user.identityType)),
    idValidatedAt: firstText(
      user.id_verified_at,
      user.identity_verified_at,
      user.id_validation_date,
      user.identity_validation_date
    ),
    identityStatusLabel: Boolean(user.id_verified)
      ? intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityStatusLabel.verified" })
      : hasIdentityDocument
        ? intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityStatusLabel.pending" })
        : intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityStatusLabel.notVerified" }),
    paymentCardCount: paymentCards.length,
    paymentMethod: formatPaymentMethod(intl, user.preferred_payment_method),
    phone: formatPhone(intl, user),
    phoneVerified: normalizeBooleanFlag(user.phone_verified),
    phoneVerifiedAt: firstText(
      user.phone_verified_at,
      user.phoneVerifiedAt,
      user.phone_activated_at,
      user.phoneActivatedAt
    ),
    deviceLabel: displayValue(
      intl,
      firstText(
        user.device_type,
        user.deviceType,
        user.device_name,
        user.deviceName,
        user.browser,
        user.platform
      ),
      intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.common.thisDevice" })
    ),
    activityLocation: displayValue(
      intl,
      firstText(
        user.city,
        defaultAddress.city,
        user.country,
        defaultAddress.country,
        user.address,
        defaultAddress.address
      ),
      intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.common.locationHidden" })
    ),
    lastActivityAt: firstText(
      user.last_login_at,
      user.lastLoginAt,
      user.last_activity_at,
      user.lastActivityAt,
      user.updated_at,
      user.updatedAt,
      user.phone_verified_at,
      user.phoneVerifiedAt,
      user.created_at
    ),
    ipAddress: displayValue(
      intl,
      firstText(user.last_ip_address, user.ip_address, user.ipAddress, user.lastIpAddress),
      intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.common.hidden" })
    ),
    progressPercent,
    rating: formatRating(intl, user.rating),
    status: formatStatus(intl, user.account_status),
    userType: displayValue(intl, user.user_type, intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.common.defaultUserType" })),
    walletBalance: formatAmount(intl, walletBalance),
  };
}

function normalizeUserStatistics(intl, statisticsDetails, profileDetails, authUser) {
  const stats = statisticsDetails || {};
  const user = profileDetails || authUser || {};

  return {
    averageRating: formatRating(
      intl,
      stats.average_rating ?? stats.averageRating ?? user.rating
    ),
    completedRidesCount: formatInteger(
      intl,
      stats.completed_rides_count ?? stats.completedRidesCount ?? user.due_trip
    ),
    loyaltyPoints: formatPoints(intl, stats.loyalty_points ?? stats.loyaltyPoints),
    totalRideTime: displayValue(
      intl,
      firstText(stats.total_ride_time, stats.totalRideTime),
      intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.common.zeroMinutes" })
    ),
  };
}

function getProfileSource(profileDetails, authUser) {
  return profileDetails || authUser || {};
}

function getSavedAddresses(user = {}) {
  return Array.isArray(user.user_fav_addresses) ? user.user_fav_addresses : [];
}

function getPaymentCards(user = {}) {
  return Array.isArray(user.payment_cards) ? user.payment_cards : [];
}

function getDefaultAddress(user = {}) {
  const savedAddresses = getSavedAddresses(user);
  return savedAddresses.find((address) => address?.is_default) || savedAddresses[0] || {};
}

function buildModalForm(intl, type, profileDetails, authUser) {
  const user = getProfileSource(profileDetails, authUser);
  const fullName = buildFullName(user);
  const nameParts = fullName.split(" ").filter(Boolean);
  const defaultAddress = getDefaultAddress(user);
  const firstCard = getPaymentCards(user)[0] || {};
  const walletBalance = firstText(
    user.user_wallets?.wallet_balance,
    user.wallet_balance
  );
  const idNumber = firstText(user.id_number, user.identity_number, user.identityNumber);

  return {
    accountStatus: firstText(user.account_status, "ACTIVE"),
    address: firstText(user.address, defaultAddress.address),
    addressLabel: firstText(defaultAddress.label, defaultAddress.name, intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.common.mainAddress" })),
    avatarUrl: firstText(user.avatar_url, user.image_local, user.image),
    cardBrand: firstText(firstCard.brand, firstCard.card_brand),
    cardHolder: firstText(firstCard.holder_name, firstCard.card_holder),
    cardLastDigits: firstText(firstCard.last4, firstCard.last_digits, firstCard.card_last_digits),
    city: firstText(user.city, defaultAddress.city),
    country: firstText(user.country, defaultAddress.country),
    countryCode: firstText(user.country_code, user.countryCode),
    email: firstText(user.email, authUser?.email),
    firstName: firstText(user.first_name, user.firstName, nameParts[0]),
    idNumber,
    identityFile: null,
    identityFileName: "",
    identitySubmittedAt: firstText(
      user.id_submitted_at,
      user.identity_submitted_at,
      user.id_uploaded_at,
      user.identity_uploaded_at
    ),
    identityType: normalizeIdentityType(firstText(user.id_type, user.identity_type, user.identityType)),
    identityValidatedAt: firstText(
      user.id_verified_at,
      user.identity_verified_at,
      user.id_validation_date,
      user.identity_validation_date
    ),
    lastName: firstText(user.last_name, user.lastName, nameParts.slice(1).join(" ")),
    paymentMethod: firstText(user.preferred_payment_method, "CASH"),
    phone: firstText(user.phone, user.mobile_number, user.mobileNumber),
    state: firstText(user.state, defaultAddress.state),
    supportMessage: "",
    supportSubject: type === "support" ? intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.common.supportSubjectDefault" }) : "",
    userType: firstText(user.user_type, intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.common.defaultUserType" })),
    walletBalance,
  };
}

function compactObject(data = {}) {
  return Object.entries(data).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null) {
      acc[key] = value;
    }

    return acc;
  }, {});
}

function resolveIdentityDocumentTypeId(identityDocumentRows = [], identityType = "") {
  const selectedType = normalizeIdentityType(identityType);

  if (!selectedType) {
    return null;
  }

  const matchedRow = (Array.isArray(identityDocumentRows) ? identityDocumentRows : []).find((document) => {
    const rowType = normalizeIdentityType(firstText(document?.documentTypeAlias, document?.documentType));
    return rowType === selectedType;
  });

  const rawDocumentTypeId = matchedRow?.documentTypeId;

  if (rawDocumentTypeId === undefined || rawDocumentTypeId === null || rawDocumentTypeId === "") {
    return null;
  }

  const numericDocumentTypeId = Number(rawDocumentTypeId);
  return Number.isNaN(numericDocumentTypeId) ? null : numericDocumentTypeId;
}

function buildModalUpdates(type, form, profileDetails, authUser, identityDocumentRows = [], driverId = "") {
  const user = getProfileSource(profileDetails, authUser);
  const fullName = [form.firstName, form.lastName].map(cleanText).filter(Boolean).join(" ");

  if (type === "profile") {
    const apiPayload = compactObject({
      avatar_url: form.avatarUrl,
      country_code: form.countryCode,
      email: form.email,
      firstName: form.firstName,
      image: form.avatarUrl,
      lastName: form.lastName,
      mobileNumber: form.phone,
      name: fullName,
      phone: form.phone,
    });

    return { apiPayload, localUpdates: apiPayload };
  }

  if (type === "identity-upload") {
    const documentTypeId = resolveIdentityDocumentTypeId(identityDocumentRows, form.identityType);
    const driverIdentifier = cleanText(driverId) || cleanText(user?._id);

    if (!driverIdentifier || !documentTypeId || !form.identityFile) {
      return { apiPayload: {}, localUpdates: {} };
    }

    const apiPayload = new FormData();
    apiPayload.append("user_id", driverIdentifier);
    apiPayload.append("document_type_id", String(documentTypeId));
    apiPayload.append("image", form.identityFile);

    const localUpdates = compactObject({
      id_number: cleanText(form.idNumber) ? form.idNumber : undefined,
      id_submitted_at: new Date().toISOString(),
      id_type: form.identityType,
      id_validated_at: "",
      id_validation_date: "",
      id_verified: false,
      id_verified_at: "",
      identity_document_name: form.identityFileName,
      identity_document_uploaded: true,
      identity_number: cleanText(form.idNumber) ? form.idNumber : undefined,
      identity_validation_date: "",
      identity_verified_at: "",
      identity_type: form.identityType,
    });

    return { apiPayload, localUpdates };
  }

  if (type === "identity-status") {
    return { apiPayload: {}, localUpdates: {} };
  }

  if (type === "personal" || type === "security") {
    const apiPayload = compactObject({
      address: form.address,
      city: form.city,
      country: form.country,
      country_code: form.countryCode,
      email: form.email,
      firstName: form.firstName,
      id_number: form.idNumber,
      identity_number: form.idNumber,
      lastName: form.lastName,
      mobileNumber: form.phone,
      name: fullName,
      phone: form.phone,
      state: form.state,
    });

    return { apiPayload, localUpdates: apiPayload };
  }

  if (type === "loyalty" || type === "account") {
    const apiPayload = compactObject({
      account_status: form.accountStatus,
      preferred_payment_method: form.paymentMethod,
      user_type: form.userType,
    });

    return { apiPayload, localUpdates: apiPayload };
  }

  if (type === "payment") {
    const existingCards = getPaymentCards(user);
    const card = compactObject({
      brand: form.cardBrand,
      card_brand: form.cardBrand,
      holder_name: form.cardHolder,
      last4: form.cardLastDigits,
    });
    const hasCard = Object.values(card).some((value) => cleanText(value));
    const apiPayload = compactObject({
      preferred_payment_method: form.paymentMethod,
    });
    const localUpdates = {
      ...apiPayload,
      payment_cards: hasCard ? [card, ...existingCards.slice(1)] : existingCards,
    };

    return { apiPayload, localUpdates };
  }

  if (type === "addresses") {
    const existingAddresses = getSavedAddresses(user);
    const favoriteAddress = compactObject({
      address: form.address,
      city: form.city,
      country: form.country,
      is_default: true,
      label: form.addressLabel,
      state: form.state,
    });
    const hasAddress = [form.address, form.city, form.state, form.country].some((value) => cleanText(value));
    const apiPayload = compactObject({
      address: form.address,
      city: form.city,
      country: form.country,
      state: form.state,
    });
    const localUpdates = {
      ...apiPayload,
      user_fav_addresses: hasAddress
        ? [favoriteAddress, ...existingAddresses.slice(1)]
        : existingAddresses,
    };

    return { apiPayload, localUpdates };
  }

  if (type === "wallet") {
    const apiPayload = compactObject({
      preferred_payment_method: form.paymentMethod,
    });

    return { apiPayload, localUpdates: apiPayload };
  }

  return { apiPayload: {}, localUpdates: {} };
}

function extractApiList(response) {
  const directData = response?.data?.data;
  if (Array.isArray(directData)) {
    return directData;
  }

  if (directData && typeof directData === "object") {
    const preferredKeys = [
      "documentTypes",
      "driverDocumentTypes",
      "driverDocuments",
      "documents",
      "items",
      "rows",
      "results",
      "data",
    ];

    for (const key of preferredKeys) {
      if (Array.isArray(directData[key])) {
        return directData[key];
      }
    }

    const firstArrayValue = Object.values(directData).find((value) => Array.isArray(value));
    if (Array.isArray(firstArrayValue)) {
      return firstArrayValue;
    }
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.data?.data?.data)) {
    return response.data.data.data;
  }

  return [];
}

function normalizeComparisonText(value) {
  return cleanText(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function getNumericId(value) {
  const text = cleanText(value);

  if (!text) {
    return null;
  }

  const numericValue = Number(text);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function getIdentityDocumentLookupKeys(documentType = {}) {
  const keys = [];
  const aliasKey = normalizeComparisonText(
    firstText(documentType?.documentTypeAlias, documentType?.documentDescription, documentType?.documentType)
  );
  const labelKey = normalizeComparisonText(firstText(documentType?.documentType, documentType?.documentName));
  const typeId = getNumericId(
    documentType?.documentTypeId ?? documentType?.id ?? documentType?.document_type_id ?? documentType?.documentType
  );

  if (aliasKey) {
    keys.push(`alias:${aliasKey}`);
  }

  if (labelKey) {
    keys.push(`label:${labelKey}`);
  }

  if (typeId !== null) {
    keys.push(`id:${typeId}`);
  }

  return [...new Set(keys)];
}

function getIdentityDocumentCanonicalKey(documentType = {}) {
  const lookupKeys = getIdentityDocumentLookupKeys(documentType);
  return lookupKeys.find((key) => key.startsWith("alias:") || key.startsWith("label:")) || lookupKeys[0] || "";
}

function getUserDocumentLookupKeys(userDocument = {}) {
  const keys = [];
  const aliasKey = normalizeComparisonText(
    firstText(userDocument?.documentDescription, userDocument?.documentTypeAlias, userDocument?.documentType)
  );
  const labelKey = normalizeComparisonText(firstText(userDocument?.documentType, userDocument?.documentName));
  const typeId = getNumericId(userDocument?.documentTypeId ?? userDocument?.document_type_id ?? userDocument?.documentType);

  if (aliasKey) {
    keys.push(`alias:${aliasKey}`);
  }

  if (labelKey) {
    keys.push(`label:${labelKey}`);
  }

  if (typeId !== null) {
    keys.push(`id:${typeId}`);
  }

  return [...new Set(keys)];
}

function getUserDocumentCanonicalKey(userDocument = {}) {
  const lookupKeys = getUserDocumentLookupKeys(userDocument);
  return lookupKeys.find((key) => key.startsWith("alias:") || key.startsWith("label:")) || lookupKeys[0] || "";
}

function getIdentityDocumentFileName(pathValue) {
  const pathText = cleanText(pathValue).replace(/\\/g, "/").split("?")[0];
  if (!pathText) {
    return "";
  }

  return pathText.split("/").filter(Boolean).pop() || pathText;
}

function resolveDocumentUrl(pathValue) {
  const pathText = cleanText(pathValue);

  if (!pathText) {
    return "";
  }

  if (/^(https?:|blob:|data:)/i.test(pathText)) {
    return pathText;
  }

  if (pathText.startsWith("/")) {
    return `${apiUrl}${pathText}`;
  }

  return `${apiUrl}/${pathText}`;
}

function getIdentityDocumentPreviewKind(pathValue) {
  const fileName = getIdentityDocumentFileName(pathValue).toLowerCase();

  if (!fileName) {
    return "file";
  }

  if (fileName.endsWith(".pdf")) {
    return "pdf";
  }

  if (/\.(png|jpe?g|webp|gif|bmp|avif)$/i.test(fileName)) {
    return "image";
  }

  return "file";
}

function formatIdentityDocumentStatus(intl, value, hasDocument = false) {
  const status = normalizeComparisonText(value);
  const labels = {
    APPROVED: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.docStatus.approved" }),
    DECLINED: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.docStatus.rejected" }),
    FAILED: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.docStatus.failed" }),
    ACCESSING: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.docStatus.submitted" }),
    CREATED: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.docStatus.submitted" }),
    UPLOADED: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.docStatus.submitted" }),
    UPLOADING: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.docStatus.inProgress" }),
    "IN REVIEW": intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.docStatus.inReview" }),
    MISSING: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.docStatus.missing" }),
    "NO DOCUMENT": intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.docStatus.noDocument" }),
    "NO DOCUMENT FOUND": intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.docStatus.noDocument" }),
    PENDING: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.docStatus.pending" }),
    PROCESSING: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.docStatus.inProgress" }),
    REJECTED: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.docStatus.rejected" }),
    REVIEW: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.docStatus.inReview" }),
    SUBMITTED: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.docStatus.submitted" }),
    VALIDATED: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.docStatus.validated" }),
    VERIFIED: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.docStatus.verified" }),
  };

  if (labels[status]) {
    return labels[status];
  }

  if (!status) {
    return hasDocument
      ? intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.docStatus.submitted" })
      : intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.docStatus.noDocument" });
  }

  return hasDocument
    ? intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.docStatus.submitted" })
    : displayValue(intl, value, intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.docStatus.noDocument" }));
}

function getIdentityDocumentStatusTone(value, hasDocument = false) {
  const status = normalizeComparisonText(value);

  if (["APPROVED", "VALIDATED", "VERIFIED"].includes(status)) {
    return "is-approved";
  }

  if (
    ["PENDING", "PROCESSING", "REVIEW", "IN REVIEW", "SUBMITTED", "ACCESSING", "CREATED", "UPLOADED", "UPLOADING"].includes(status)
  ) {
    return "is-pending";
  }

  if (["DECLINED", "FAILED", "REJECTED"].includes(status)) {
    return "is-rejected";
  }

  return hasDocument ? "is-pending" : "is-neutral";
}

async function fetchIdentityDocumentPayload(userId) {
  const [documentTypesResponse, userDocumentsResponse] = await Promise.all([
    profileApi.getCorporateDocumentTypes(),
    profileApi.getCorporateDocumentsByDriverId(userId),
  ]);

  return {
    documentTypes: extractApiList(documentTypesResponse),
    documents: extractApiList(userDocumentsResponse),
  };
}
function IdentityDocumentStatusPill({ value, hasDocument = false }) {
  const intl = useIntl();
  return (
    <span className={`pc-document-status ${getIdentityDocumentStatusTone(value, hasDocument)}`.trim()}>
      {formatIdentityDocumentStatus(intl, value, hasDocument)}
    </span>
  );
}

function IdentityDocumentRow({ document, isSelected = false, onSelect }) {
  const intl = useIntl();
  const documentPath = document?.file || document?.documentPath || "";
  const hasDocument = Boolean(cleanText(documentPath));
  const title = firstText(document?.documentType, document?.documentDescription) || intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityDoc.document" });
  const subtitle = firstText(
    document?.documentTypeAlias,
    document?.documentDescription,
    document?.documentTypeId ? intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityDoc.typeN" }, { id: document.documentTypeId }) : "",
    document?.documentType
  );
  const fileLabel = hasDocument
    ? firstText(
        document?.documentName,
        getIdentityDocumentFileName(documentPath),
        document?.documentDescription
      )
    : intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityDoc.noFileUploaded" });
  const statusTone = getIdentityDocumentStatusTone(document?.status, hasDocument);
  const statusLabel = formatIdentityDocumentStatus(intl, document?.status, hasDocument);
  const rowClassName = [
    "pc-identity-document-item",
    hasDocument ? "has-document" : "is-empty",
    hasDocument ? statusTone : "",
    isSelected ? "is-selected" : "",
    onSelect ? "is-clickable" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      <div className="pc-identity-document-copy">
        <strong>{title}</strong>
        <span>{subtitle}</span>
      </div>
      <div className="pc-identity-document-meta">
        <IdentityDocumentStatusPill value={document?.status} hasDocument={hasDocument} />
        <small>{fileLabel}</small>
        {document?.updatedAt ? (
          <small>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityDoc.updatedOn" }, { date: formatDateTime(intl, document.updatedAt) })}</small>
        ) : null}
        {!document?.status && hasDocument ? <small>{statusLabel}</small> : null}
      </div>
    </>
  );

  if (onSelect) {
    return (
      <button
        type="button"
        className={rowClassName}
        onClick={() => onSelect(document)}
        aria-pressed={isSelected}
      >
        {content}
      </button>
    );
  }

  return <article className={rowClassName}>{content}</article>;
}

function IdentityDocumentsCard({
  documents = [],
  emptyMessage = "",
  error = "",
  isLoading = false,
  onSelectDocument = null,
  selectedDocumentKey = "",
  subtitle = "",
  title = "",
}) {
  const intl = useIntl();
  const hasDocuments = Array.isArray(documents) && documents.length > 0;
  const resolvedEmptyMessage = emptyMessage || intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityDoc.noDocumentAvailable" });

  return (
    <section className="pc-identity-documents-card">
        <div className="pc-identity-documents-head">
        <div>
          <span className="pc-identity-block-label">{title}</span>
          <strong>{subtitle}</strong>
        </div>
        <span className="pc-identity-documents-count">
          {hasDocuments
            ? pluralize(
                documents.length,
                intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityDoc.documentSingular" }),
                intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityDoc.documentPlural" })
              )
            : intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityDoc.zeroDocuments" })}
        </span>
      </div>

      {error ? <div className="pc-identity-documents-state is-error">{error}</div> : null}

      {isLoading && !hasDocuments ? (
        <div className="pc-identity-documents-state">{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityDoc.loadingDocuments" })}</div>
      ) : hasDocuments ? (
        <div className="pc-identity-documents-list">
          {documents.map((document, index) => (
            <IdentityDocumentRow
              key={document?.key || document?._id || document?.documentTypeId || document?.id || index}
              document={document}
              isSelected={document?._key === selectedDocumentKey}
              onSelect={onSelectDocument}
            />
          ))}
        </div>
      ) : (
        <div className="pc-identity-documents-state">{resolvedEmptyMessage}</div>
      )}
    </section>
  );
}

function CameraIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7.5 7.5h2l1.2-2h2.6l1.2 2h2a2 2 0 0 1 2 2v8.5a2 2 0 0 1-2 2h-9a2 2 0 0 1-2-2V9.5a2 2 0 0 1 2-2Z" />
      <circle cx="12" cy="13" r="3.2" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="m8.8 12.1 2.1 2.1 4.4-4.8" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 15V5" />
      <path d="m8.5 8.5 3.5-3.5 3.5 3.5" />
      <path d="M5 16.5v1A2.5 2.5 0 0 0 7.5 20h9A2.5 2.5 0 0 0 19 17.5v-1" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4" y="5" width="16" height="15" rx="2.5" />
      <path d="M8 3.5v3M16 3.5v3M4 10h16" />
    </svg>
  );
}

function CardIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4" y="6" width="16" height="12" rx="2.5" />
      <path d="M4 10h16" />
    </svg>
  );
}

function DeviceIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="6" y="3.5" width="12" height="17" rx="2.5" />
      <path d="M10 18h4" />
    </svg>
  );
}

function CarIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 13.5 6.4 9.8A2.7 2.7 0 0 1 9 8h6a2.7 2.7 0 0 1 2.6 1.8l1.4 3.7" />
      <path d="M4 13.5h16V17H4z" />
      <circle cx="8" cy="16.8" r="1.2" />
      <circle cx="16" cy="16.8" r="1.2" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m12 4 2.7 5.4 5.9.8-4.3 4.1 1 5.8-5.3-2.9-5.3 2.9 1-5.8L3.4 10l5.9-.8L12 4Z" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3.5 19 6.4v5.2c0 4.4-3 7.8-7 9.4-4-1.6-7-5-7-9.4V6.4L12 3.5Z" />
      <path d="m8.5 12.1 2.4 2.4 4.8-5" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="6.5" y="10.5" width="11" height="8" rx="2" />
      <path d="M9 10.5V8a3 3 0 1 1 6 0v2.5" />
    </svg>
  );
}

function MiniCheckIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m6.8 12.2 3.1 3.1 7.4-7.9" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7.5 4.5h2L11 8l-1.8 1.8a13 13 0 0 0 4 4L15 12l3.5 1.5v2a2 2 0 0 1-2 2A12.5 12.5 0 0 1 4.5 7.5a2 2 0 0 1 2-2Z" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4" y="6" width="16" height="12" rx="2.5" />
      <path d="m5.5 8.5 6.5 5 6.5-5" />
    </svg>
  );
}

function LocationIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 21s5-5.6 5-10.5A5 5 0 0 0 7 10.5C7 15.4 12 21 12 21Z" />
      <circle cx="12" cy="10.5" r="2" />
    </svg>
  );
}

function IdIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4" y="6" width="16" height="12" rx="2.5" />
      <circle cx="9" cy="11" r="1.8" />
      <path d="M13 10.5h4M13 13.5h4" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M10 4.5H6.8A2.3 2.3 0 0 0 4.5 6.8v10.4a2.3 2.3 0 0 0 2.3 2.3H10" />
      <path d="m13 8 4 4-4 4M17 12H9" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m6 6 12 12M18 6 6 18" />
    </svg>
  );
}

function ArrowRow() {
  return <span className="pc-chevron" aria-hidden="true"><ChevronIcon /></span>;
}

function HeadAction({ children, onClick }) {
  return (
    <button type="button" className="pc-head-action" onClick={onClick}>
      {children}
    </button>
  );
}

function StatItem({ icon, value, label, tone, onClick }) {
  const Icon = icon;
  const content = (
    <>
      <span className={`pc-stat-icon ${tone}`}>
        <Icon />
      </span>
      <div>
        <strong>{value}</strong>
        <span>{label}</span>
      </div>
    </>
  );

  if (onClick) {
    return (
      <button type="button" className="pc-stat-item is-clickable" onClick={onClick}>
        {content}
      </button>
    );
  }

  return (
    <div className="pc-stat-item">
      {content}
    </div>
  );
}

function InfoRow({ icon, label, value, verified = false, actionLabel = "", actionIcon: ActionIcon, actionClassName = "", onClick }) {
  const intl = useIntl();
  const Icon = icon;
  const content = (
    <>
      <span className="pc-info-icon">
        <Icon />
      </span>
      <span className="pc-info-label">{label}</span>
      <span className="pc-info-value">
        {value}
        {verified ? (
          <span className="pc-verified-pill">
            {intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.common.verified" })} <CheckIcon />
          </span>
        ) : null}
        {!verified && actionLabel ? (
          <span className={`pc-action-pill ${actionClassName}`.trim()}>
            <span>{actionLabel}</span>
            {ActionIcon ? <ActionIcon /> : null}
          </span>
        ) : null}
      </span>
      <ArrowRow />
    </>
  );

  if (onClick) {
    return (
      <button type="button" className="pc-info-row is-clickable" onClick={onClick}>
        {content}
      </button>
    );
  }

  return (
    <div className="pc-info-row">
      {content}
    </div>
  );
}

function RightItem({ icon, title, desc, onClick }) {
  const Icon = icon;
  const content = (
    <>
      <span className="pc-right-icon">
        <Icon />
      </span>
      <div className="pc-right-copy">
        <strong>{title}</strong>
        <span>{desc}</span>
      </div>
      <ArrowRow />
    </>
  );

  if (onClick) {
    return (
      <button type="button" className="pc-right-item is-clickable" onClick={onClick}>
        {content}
      </button>
    );
  }

  return (
    <div className="pc-right-item">
      {content}
    </div>
  );
}

function ModalField({ label, name, value, onChange, placeholder = "", type = "text" }) {
  return (
    <label className="pc-modal-field">
      <span>{label}</span>
      <input
        type={type}
        name={name}
        value={value || ""}
        placeholder={placeholder}
        onChange={(event) => onChange(name, event.target.value)}
      />
    </label>
  );
}

function ModalSelect({ label, name, value, onChange, options }) {
  return (
    <label className="pc-modal-field">
      <span>{label}</span>
      <select
        name={name}
        value={value || ""}
        onChange={(event) => onChange(name, event.target.value)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function ModalTextarea({ label, name, value, onChange, placeholder = "" }) {
  return (
    <label className="pc-modal-field is-wide">
      <span>{label}</span>
      <textarea
        name={name}
        value={value || ""}
        placeholder={placeholder}
        rows={4}
        onChange={(event) => onChange(name, event.target.value)}
      />
    </label>
  );
}

function ModalSummaryItem({ label, value }) {
  return (
    <div className="pc-modal-summary-item">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function OtpInputGroup({ value = [], onChange }) {
  const intl = useIntl();
  const inputRefs = useRef([]);
  const digits = Array.from({ length: 6 }, (_, index) => cleanText(value?.[index]).replace(/\D/g, "").slice(-1));

  const focusInput = (index) => {
    inputRefs.current[index]?.focus();
  };

  const updateDigits = (nextDigits) => {
    onChange(Array.from({ length: 6 }, (_, index) => cleanText(nextDigits?.[index]).replace(/\D/g, "").slice(-1)));
  };

  const handleChange = (index, rawValue) => {
    const digit = cleanText(rawValue).replace(/\D/g, "").slice(-1);
    const nextDigits = [...digits];
    nextDigits[index] = digit;
    updateDigits(nextDigits);

    if (digit && index < 5) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      focusInput(index - 1);
    }
  };

  const handlePaste = (event) => {
    const pastedDigits = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6)
      .split("");

    if (!pastedDigits.length) {
      return;
    }

    event.preventDefault();

    const nextDigits = Array.from({ length: 6 }, (_, index) => pastedDigits[index] || digits[index] || "");
    updateDigits(nextDigits);
    focusInput(Math.min(pastedDigits.length, 5));
  };

  return (
    <div className="pc-otp-grid" onPaste={handlePaste}>
      {digits.map((digit, index) => (
        <input
          key={`otp-${index}`}
          ref={(node) => {
            inputRefs.current[index] = node;
          }}
          className="pc-otp-digit"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus={index === 0}
          maxLength={1}
          value={digit}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          aria-label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.otp.digitLabel" }, { index: index + 1 })}
        />
      ))}
    </div>
  );
}

function StatusDetailRow({ icon, label, value }) {
  const Icon = icon;

  return (
    <div className="pc-phone-status-detail">
      <span className="pc-phone-status-detail-icon" aria-hidden="true">
        <Icon />
      </span>
      <div className="pc-phone-status-detail-copy">
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
    </div>
  );
}

function ProfileModal({
  children,
  feedback,
  className = "",
  isSaving,
  onClose,
  onSubmit,
  open,
  submitLabel,
  title,
}) {
  const intl = useIntl();
  if (!open) {
    return null;
  }

  return (
    <div className="pc-modal-backdrop" role="presentation">
      <div className={`pc-modal ${className}`.trim()} role="dialog" aria-modal="true" aria-labelledby="pc-modal-title">
        <form onSubmit={onSubmit}>
          <div className="pc-modal-head">
            <h3 id="pc-modal-title">{title}</h3>
            <button type="button" className="pc-modal-close" onClick={onClose} aria-label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.modal.close" })}>
              <CloseIcon />
            </button>
          </div>

          <div className="pc-modal-body">
            {children}
            {feedback ? <div className="pc-modal-feedback">{feedback}</div> : null}
          </div>

          <div className="pc-modal-actions">
            <button type="button" className="pc-modal-secondary" onClick={onClose}>
              {submitLabel
                ? intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.modal.cancel" })
                : intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.modal.close" })}
            </button>
            {submitLabel ? (
              <button type="submit" className="pc-modal-primary" disabled={isSaving}>
                {isSaving ? intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.modal.saving" }) : submitLabel}
              </button>
            ) : null}
          </div>
        </form>
      </div>
    </div>
  );
}
export default function ProfileEntreprise() {
  const intl = useIntl();
  const authUser = useSelector((state) => state?.AuthReducer?.user || {});
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const userId = useSelector(
    (state) =>
      state?.AuthReducer?.userid ||
      state?.AuthReducer?.user?.id ||
      localStorage.getItem("userid") ||
      ""
  );
  const [profileDetails, setProfileDetails] = useState(null);
  const [statisticsDetails, setStatisticsDetails] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [modalForm, setModalForm] = useState({});
  const [modalFeedback, setModalFeedback] = useState("");
  const [identityPreviewUrl, setIdentityPreviewUrl] = useState("");
  const [phoneOtpDigits, setPhoneOtpDigits] = useState(Array(6).fill(""));
  const [phoneOtpSecondsLeft, setPhoneOtpSecondsLeft] = useState(165);
  const [phoneOtpResendSecondsLeft, setPhoneOtpResendSecondsLeft] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isAvatarUploading, setIsAvatarUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [identityDocumentTypes, setIdentityDocumentTypes] = useState([]);
  const [identityDocuments, setIdentityDocuments] = useState([]);
  const [identityDocumentsLoading, setIdentityDocumentsLoading] = useState(false);
  const [identityDocumentsError, setIdentityDocumentsError] = useState("");
  const [selectedIdentityDocumentKey, setSelectedIdentityDocumentKey] = useState("");
  const profileAvatarInputRef = useRef(null);
  const identityDocumentsFetchRef = useRef(0);
  const walletModalAutoOpenRef = useRef(false);

  const refreshIdentityDocuments = useCallback(
    (targetUserId = userId) => {
      if (!targetUserId) {
        setIdentityDocumentTypes([]);
        setIdentityDocuments([]);
        setIdentityDocumentsError("");
        setIdentityDocumentsLoading(false);
        return Promise.resolve({ documentTypes: [], documents: [] });
      }

      const requestId = identityDocumentsFetchRef.current + 1;
      identityDocumentsFetchRef.current = requestId;

      setIdentityDocumentsLoading(true);
      setIdentityDocumentsError("");

      return fetchIdentityDocumentPayload(targetUserId)
        .then((payload) => {
          if (identityDocumentsFetchRef.current !== requestId) {
            return payload;
          }

          setIdentityDocumentTypes(payload.documentTypes);
          setIdentityDocuments(payload.documents);
          return payload;
        })
        .catch(() => {
          if (identityDocumentsFetchRef.current === requestId) {
            setIdentityDocumentsError(intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.errors.loadDocumentsFailed" }));
          }

          return { documentTypes: [], documents: [] };
        })
        .finally(() => {
          if (identityDocumentsFetchRef.current === requestId) {
            setIdentityDocumentsLoading(false);
          }
        });
    },
    [userId, intl]
  );

  const handleIdentityDrop = (acceptedFiles) => {
    const [file] = acceptedFiles || [];

    if (!file) {
      return;
    }

    setModalForm((currentForm) => ({
      ...currentForm,
      identityFile: file,
      identityFileName: file.name,
    }));
    setModalFeedback("");
  };

  const handleOpenProfileAvatarPicker = () => {
    if (isAvatarUploading) {
      return;
    }

    profileAvatarInputRef.current?.click();
  };

  const handleProfileAvatarChange = (event) => {
    const [file] = event.target.files || [];
    event.target.value = "";

    if (!file) {
      return;
    }

    if (!file.type?.startsWith("image/")) {
      dispatch(
        showSnackbar({
          message: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.errors.invalidImageType" }),
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
      return;
    }

    if (file.size > maxIdentityFileSize) {
      dispatch(
        showSnackbar({
          message: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.errors.photoTooLarge" }),
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
      return;
    }

    if (!userId) {
      dispatch(
        showSnackbar({
          message: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.errors.accountNotFound" }),
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
      return;
    }

    setIsAvatarUploading(true);
    setModalFeedback("");

    profileApi
      .updateCorporateById(userId, { image: file })
      .then((response) => {
        dispatch(
          showSnackbar({
            message: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.success.avatarUpdated" }),
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );

        const responseProfile =
          response?.data?.data?.user ||
          response?.data?.data ||
          response?.data?.user ||
          null;

        if (responseProfile && typeof responseProfile === "object" && !Array.isArray(responseProfile)) {
          setProfileDetails((currentProfile) => ({
            ...(currentProfile || {}),
            ...responseProfile,
          }));
        }

        return profileApi
          .getUserDetailsById(userId)
          .then((refreshResponse) => {
            const refreshedProfile = refreshResponse?.data?.data || refreshResponse?.data || null;
            if (refreshedProfile) {
              setProfileDetails(refreshedProfile);
            }
          })
          .catch(() => {
            // Keep the current profile state if the refresh fails.
          });
      })
      .catch(() => {
        dispatch(
          showSnackbar({
            message: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.errors.avatarUpdateFailed" }),
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "error",
          })
        );
      })
      .finally(() => {
        setIsAvatarUploading(false);
      });
  };

  const {
    getRootProps: getIdentityRootProps,
    getInputProps: getIdentityInputProps,
    open: openIdentityFilePicker,
  } = useDropzone({
    accept: {
      "application/pdf": [".pdf"],
      "image/*": [".jpeg", ".jpg", ".png"],
    },
    maxFiles: 1,
    maxSize: maxIdentityFileSize,
    noClick: true,
    noKeyboard: true,
    onDrop: handleIdentityDrop,
    onDropRejected: () => {
      setModalFeedback(intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.errors.invalidIdentityFile" }));
    },
  });

  useEffect(() => {
    if (!modalForm.identityFile) {
      setIdentityPreviewUrl("");
      return undefined;
    }

    const previewUrl = URL.createObjectURL(modalForm.identityFile);
    setIdentityPreviewUrl(previewUrl);

    return () => {
      URL.revokeObjectURL(previewUrl);
    };
  }, [modalForm.identityFile]);

  useEffect(() => {
    if (activeModal !== "phone-activation") {
      return undefined;
    }

    const interval = window.setInterval(() => {
      setPhoneOtpSecondsLeft((current) => Math.max(current - 1, 0));
      setPhoneOtpResendSecondsLeft((current) => Math.max(current - 1, 0));
    }, 1000);

    return () => {
      window.clearInterval(interval);
    };
  }, [activeModal]);

  useEffect(() => {
    if (walletModalAutoOpenRef.current) {
      return undefined;
    }

    if (searchParams.get("open") !== "wallet") {
      return undefined;
    }

    if (!profileDetails) {
      return undefined;
    }

    walletModalAutoOpenRef.current = true;
    setActiveModal("wallet");
    setModalForm(buildModalForm(intl, "wallet", profileDetails, authUser));
    setModalFeedback("");

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("open");
    setSearchParams(nextParams, { replace: true });

    return undefined;
  }, [authUser, profileDetails, searchParams, setSearchParams, intl]);

  useEffect(() => {
    if (!userId) {
      setErrorMessage(intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.errors.userNotFound" }));
      return undefined;
    }

    let isMounted = true;
    setIsLoading(true);
    setErrorMessage("");

    profileApi
      .getUserDetailsById(userId)
      .then((response) => {
        if (!isMounted) {
          return;
        }

        setProfileDetails(response?.data?.data || response?.data || null);
      })
      .catch(() => {
        if (isMounted) {
          setErrorMessage(intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.errors.loadProfileFailed" }));
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [userId, intl]);

  useEffect(() => {
    if (!userId) {
      return undefined;
    }

    let isMounted = true;

    profileApi
      .getUserStatisticsById(userId)
      .then((response) => {
        if (!isMounted) {
          return;
        }

        setStatisticsDetails(response?.data?.data || response?.data || null);
      })
      .catch(() => {
        if (isMounted) {
          setStatisticsDetails(null);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setIdentityDocumentTypes([]);
      setIdentityDocuments([]);
      setIdentityDocumentsError("");
      setIdentityDocumentsLoading(false);
      return undefined;
    }

    refreshIdentityDocuments();

    return undefined;
  }, [refreshIdentityDocuments, userId]);

  const profile = useMemo(
    () => normalizeUserProfile(intl, profileDetails, authUser),
    [intl, profileDetails, authUser]
  );
  const statistics = useMemo(
    () => normalizeUserStatistics(intl, statisticsDetails, profileDetails, authUser),
    [intl, statisticsDetails, profileDetails, authUser]
  );
  const identityTypeOptions = useMemo(() => getIdentityTypeOptions(intl), [intl]);
  const accountStatusOptions = useMemo(() => getAccountStatusOptions(intl), [intl]);
  const paymentMethodOptions = useMemo(() => getPaymentMethodOptions(intl), [intl]);
  const identityDocumentTypeOptions = useMemo(() => {
    const mappedOptions = identityDocumentTypes
      .map((documentType) => {
        const value = normalizeIdentityType(firstText(documentType?.documentTypeAlias, documentType?.documentType));
        const label = firstText(documentType?.documentType, formatIdentityType(intl, documentType?.documentTypeAlias));

        if (!value || !label) {
          return null;
        }

        return { label, value };
      })
      .filter(Boolean);

    if (!mappedOptions.length) {
      return identityTypeOptions;
    }

    const uniqueOptions = [];
    const seenValues = new Set();

    mappedOptions.forEach((option) => {
      if (seenValues.has(option.value)) {
        return;
      }

      seenValues.add(option.value);
      uniqueOptions.push(option);
    });

    return uniqueOptions;
  }, [identityDocumentTypes, identityTypeOptions, intl]);
  const effectiveIdentityTypeOptions = identityDocumentTypeOptions.length
    ? identityDocumentTypeOptions
    : identityTypeOptions;
  const identityDocumentRows = useMemo(() => {
    const sortedUploadedDocuments = [...(Array.isArray(identityDocuments) ? identityDocuments : [])].sort(
      (left, right) => {
        const leftDate = new Date(left?.updatedAt || left?.createdAt || 0).getTime();
        const rightDate = new Date(right?.updatedAt || right?.createdAt || 0).getTime();
        return rightDate - leftDate;
      }
    );
    const documentsByKey = new Map();
    const seenLookupKeys = new Set();
    const rows = [];

    sortedUploadedDocuments.forEach((document) => {
      getUserDocumentLookupKeys(document).forEach((key) => {
        if (!documentsByKey.has(key)) {
          documentsByKey.set(key, document);
        }
      });
    });

    const registerSeenKeys = (keys = []) => {
      keys.forEach((key) => {
        if (key) {
          seenLookupKeys.add(key);
        }
      });
    };

    const findMatchingDocument = (keys = []) => {
      for (const key of keys) {
        const document = documentsByKey.get(key);
        if (document) {
          return document;
        }
      }

      return null;
    };

    (Array.isArray(identityDocumentTypes) ? identityDocumentTypes : []).forEach((documentType, index) => {
      const lookupKeys = getIdentityDocumentLookupKeys(documentType);
      const canonicalKey = getIdentityDocumentCanonicalKey(documentType) || `type:${index}`;

      if (lookupKeys.some((key) => seenLookupKeys.has(key))) {
        return;
      }

      const matchingDocument = findMatchingDocument(lookupKeys);
      const documentPath = matchingDocument?.documentPath || documentType?.file || "";
      const documentLabel = firstText(
        documentType?.documentType,
        matchingDocument?.documentDescription,
        matchingDocument?.documentType
      ) || intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityDoc.document" });

      rows.push({
        _key: canonicalKey,
        documentName: firstText(
          matchingDocument?.documentDescription,
          documentType?.documentName,
          getIdentityDocumentFileName(documentPath)
        ),
        documentPath,
        documentType: documentLabel,
        documentTypeAlias: firstText(
          documentType?.documentTypeAlias,
          matchingDocument?.documentDescription,
          matchingDocument?.documentType
        ),
        documentTypeId:
          documentType?.documentTypeId ?? documentType?.id ?? matchingDocument?.documentTypeId ?? matchingDocument?.documentType ?? null,
        file: documentPath,
        hasDocument: Boolean(cleanText(documentPath)),
        status: firstText(matchingDocument?.status, documentType?.status, "No document found"),
        updatedAt: matchingDocument?.updatedAt || matchingDocument?.createdAt || "",
      });

      registerSeenKeys(lookupKeys);
      if (matchingDocument) {
        registerSeenKeys(getUserDocumentLookupKeys(matchingDocument));
      }
    });

    sortedUploadedDocuments.forEach((document) => {
      const lookupKeys = getUserDocumentLookupKeys(document);
      const canonicalKey = getUserDocumentCanonicalKey(document) || `user:${rows.length}`;

      if (lookupKeys.some((key) => seenLookupKeys.has(key))) {
        return;
      }

      rows.push({
        _key: canonicalKey,
        documentName: firstText(document?.documentDescription, getIdentityDocumentFileName(document?.documentPath)),
        documentPath: document?.documentPath || "",
        documentType: firstText(document?.documentDescription, document?.documentType) || intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityDoc.document" }),
        documentTypeAlias: firstText(document?.documentDescription, document?.documentType),
        documentTypeId: document?.documentTypeId ?? document?.document_type_id ?? document?.documentType ?? null,
        file: document?.documentPath || "",
        hasDocument: Boolean(cleanText(document?.documentPath)),
        status: firstText(document?.status, "No document found"),
        updatedAt: document?.updatedAt || document?.createdAt || "",
      });

      registerSeenKeys(lookupKeys);
    });

    return rows;
  }, [identityDocumentTypes, identityDocuments, intl]);

  const selectedIdentityDocument = useMemo(
    () => identityDocumentRows.find((document) => document?._key === selectedIdentityDocumentKey) || null,
    [identityDocumentRows, selectedIdentityDocumentKey]
  );

  const selectedIdentityDocumentUrl = useMemo(
    () => resolveDocumentUrl(selectedIdentityDocument?.file || selectedIdentityDocument?.documentPath),
    [selectedIdentityDocument]
  );

  const selectedIdentityDocumentPreviewKind = useMemo(
    () => getIdentityDocumentPreviewKind(selectedIdentityDocument?.file || selectedIdentityDocument?.documentPath),
    [selectedIdentityDocument]
  );

  useEffect(() => {
    if (activeModal !== "identity-upload" && selectedIdentityDocumentKey) {
      setSelectedIdentityDocumentKey("");
    }
  }, [activeModal, selectedIdentityDocumentKey]);

  const handleSelectIdentityDocument = (document) => {
    if (!document) {
      return;
    }

    setSelectedIdentityDocumentKey(document?._key || "");
    setModalForm((currentForm) => ({
      ...currentForm,
      identityType: normalizeIdentityType(firstText(document?.documentTypeAlias, document?.documentType, currentForm.identityType)),
    }));
    setModalFeedback("");
  };

  const handleOpenSelectedIdentityDocument = () => {
    if (selectedIdentityDocumentUrl) {
      window.open(selectedIdentityDocumentUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleDeleteSelectedIdentityDocument = () => {
    if (!selectedIdentityDocument) {
      return;
    }

    const documentTypeIdText = cleanText(selectedIdentityDocument?.documentTypeId);
    const documentTypeId = documentTypeIdText ? Number(documentTypeIdText) : Number.NaN;

    if (!Number.isFinite(documentTypeId)) {
      setModalFeedback(intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.errors.deleteDocumentTypeMissing" }));
      return;
    }

    const confirmDelete = window.confirm(intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.confirm.deleteDocument" }));
    if (!confirmDelete) {
      return;
    }

    setIsSaving(true);
    setModalFeedback("");

    driverApi
      .deleteDriverDocument({
        document_type_id: documentTypeId,
        user_id: userId,
      })
      .then((response) => {
        if (response?.status === 200 || response?.data?.status === true) {
          dispatch(
            showSnackbar({
              message: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.success.documentDeleted" }),
              autoHideDuration: 3000,
              anchorOrigin: {
                vertical: "top",
                horizontal: "right",
              },
              variant: "success",
            })
          );
          setSelectedIdentityDocumentKey("");
          setModalForm((currentForm) => ({
            ...currentForm,
            identityFile: null,
            identityFileName: "",
          }));
          refreshIdentityDocuments(userId);
        }
      })
      .catch(() => {
        dispatch(
          showSnackbar({
            message: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.errors.deleteDocumentFailed" }),
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "error",
          })
        );
        setModalFeedback(intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.errors.deleteDocumentFailed" }));
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const openProfileModal = (type) => {
    setActiveModal(type);
    setModalForm(buildModalForm(intl, type, profileDetails, authUser));
    setModalFeedback("");
    setSelectedIdentityDocumentKey("");
  };

  const openPhoneActivationModal = () => {
    setActiveModal("phone-activation");
    setModalForm(buildModalForm(intl, "personal", profileDetails, authUser));
    setPhoneOtpDigits(Array(6).fill(""));
    setPhoneOtpSecondsLeft(165);
    setPhoneOtpResendSecondsLeft(30);
    setModalFeedback("");
  };

  const openPhoneStatusModal = () => {
    setActiveModal("phone-status");
    setModalForm(buildModalForm(intl, "profile", profileDetails, authUser));
    setModalFeedback("");
  };

  const openEmailVerificationModal = () => {
    setModalFeedback("");

    const form = buildModalForm(intl, "personal", profileDetails, authUser);
    const email = firstText(profileDetails?.email, authUser?.email, form.email);

    setModalForm(form);

    if (!email) {
      setActiveModal("email-verification");
      setModalFeedback(intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.errors.emailNotFound" }));
      return;
    }

    profileApi
      .requestEmailOtp({ email })
      .then(() => {
        setActiveModal("email-verification");
      })
      .catch(() => {
        setActiveModal("email-verification");
        setModalFeedback(intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.errors.emailOtpSendFailed" }));
      });
  };

  const openIdentityModal = () => {
    openProfileModal(profile.idVerified ? "identity-status" : "identity-upload");
  };

  const closeProfileModal = () => {
    setActiveModal(null);
    setModalForm({});
    setModalFeedback("");
    setIdentityPreviewUrl("");
    setSelectedIdentityDocumentKey("");
    setPhoneOtpDigits(Array(6).fill(""));
    setPhoneOtpSecondsLeft(165);
    setPhoneOtpResendSecondsLeft(0);
    setIsSaving(false);
  };

  const updateModalForm = (name, value) => {
    setModalForm((currentForm) => ({
      ...currentForm,
      [name]: value,
    }));
  };

  const updatePhoneOtpDigits = (nextDigits) => {
    setPhoneOtpDigits(
      Array.from({ length: 6 }, (_, index) => cleanText(nextDigits?.[index]).replace(/\D/g, "").slice(-1))
    );
    setModalFeedback("");
  };

  const resendPhoneOtp = () => {
    if (phoneOtpResendSecondsLeft > 0) {
      return;
    }

    setPhoneOtpDigits(Array(6).fill(""));
    setPhoneOtpSecondsLeft(165);
    setPhoneOtpResendSecondsLeft(30);
    setModalFeedback("");
  };

  const mergeProfileDetails = (updates) => {
    setProfileDetails((currentProfile) => ({
      ...(authUser || {}),
      ...(currentProfile || {}),
      ...updates,
    }));
  };
  const handleSaveModal = (event) => {
    event.preventDefault();

    if (!activeModal || activeModal === "stats" || activeModal === "identity-status") {
      closeProfileModal();
      return;
    }

    if (activeModal === "phone-status") {
      closeProfileModal();
      return;
    }

    if (activeModal === "phone-activation") {
      const otpCode = phoneOtpDigits.join("");

      if (phoneOtpSecondsLeft <= 0) {
        setModalFeedback(intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.otp.expired" }));
        return;
      }

      if (!/^\d{6}$/.test(otpCode)) {
        setModalFeedback(intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.otp.invalidCode" }));
        return;
      }

      mergeProfileDetails({
        phone_verified: true,
        phone_verified_at: new Date().toISOString(),
      });
      closeProfileModal();
      return;
    }

    if (activeModal === "email-verification") {
      const otpCode = phoneOtpDigits.join("");
      const email = firstText(modalForm.email, profileDetails?.email, authUser?.email);

      if (phoneOtpSecondsLeft <= 0) {
        setModalFeedback(intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.otp.expired" }));
        return;
      }

      if (!/^\d{6}$/.test(otpCode)) {
        setModalFeedback(intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.otp.invalidCode" }));
        return;
      }

      if (!email) {
        setModalFeedback(intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.errors.emailNotFound" }));
        return;
      }

      profileApi
        .verifyEmailOtp({
          email,
          otp: otpCode,
        })
        .then(() => {
          mergeProfileDetails({
            email_verified: true,
          });
          closeProfileModal();
        })
        .catch(() => {
          setModalFeedback(intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.errors.emailOtpVerifyFailed" }));
        });

      return;
    }

    if (activeModal === "identity-upload") {
      if (!modalForm.identityFile) {
        setModalFeedback(intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.errors.identityFileRequired" }));
        return;
      }

      const selectedDocumentTypeId = resolveIdentityDocumentTypeId(
        identityDocumentRows,
        modalForm.identityType
      );

      if (!selectedDocumentTypeId) {
        setModalFeedback(intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.errors.identityTypeInvalid" }));
        return;
      }
    }

    if (activeModal === "support") {
      const existingRequests = Array.isArray(profileDetails?.support_requests)
        ? profileDetails.support_requests
        : [];
      mergeProfileDetails({
        support_requests: [
          {
            message: modalForm.supportMessage,
            subject: modalForm.supportSubject,
          },
          ...existingRequests,
        ],
      });
      closeProfileModal();
      return;
    }

    const { apiPayload, localUpdates } = buildModalUpdates(
      activeModal,
      modalForm,
      profileDetails,
      authUser,
      identityDocumentRows,
      userId
    );

    if (!Object.keys(localUpdates).length) {
      closeProfileModal();
      return;
    }

    setIsSaving(true);
    setModalFeedback("");

    const request =
      activeModal === "identity-upload"
        ? driverApi.updateCorporateDocument(apiPayload)
        : profileApi.updateCorporateById(userId, apiPayload);

    request
      .then((response) => {
        const responseProfile =
          response?.data?.data?.user ||
          response?.data?.data ||
          response?.data?.user ||
          null;
        const responseUpdates =
          responseProfile && typeof responseProfile === "object" && !Array.isArray(responseProfile)
            ? responseProfile
            : {};

        mergeProfileDetails({
          ...localUpdates,
          ...responseUpdates,
        });
        if (activeModal === "identity-upload") {
          refreshIdentityDocuments(userId);
        }
        closeProfileModal();
      })
      .catch(() => {
        setModalFeedback(intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.errors.saveFailed" }));
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const modalTitle = {
    account: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.modal.title.account" }),
    addresses: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.modal.title.addresses" }),
    loyalty: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.modal.title.loyalty" }),
    "phone-status": intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.modal.title.phoneStatus" }),
    "phone-activation": intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.modal.title.verifyEmail" }),
    "email-verification": intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.modal.title.verifyEmail" }),
    payment: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.modal.title.payment" }),
    personal: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.modal.title.personal" }),
    profile: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.modal.title.profile" }),
    security: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.modal.title.security" }),
    stats: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.modal.title.stats" }),
    support: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.modal.title.support" }),
    wallet: intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.modal.title.wallet" }),
    "identity-status": intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.modal.title.identityStatus" }),
    "identity-upload": intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.modal.title.identityUpload" }),
  }[activeModal] || intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.modal.title.default" });

  const modalSubmitLabel =
    activeModal === "stats" || activeModal === "identity-status"
      ? ""
      : activeModal === "phone-status"
        ? intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.modal.submitLabel.continue" })
      : activeModal === "identity-upload"
        ? intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.modal.submitLabel.sendForVerification" })
        : activeModal === "phone-activation"
          ? intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.modal.title.verifyEmail" })
        : activeModal === "email-verification"
          ? intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.modal.title.verifyEmail" })
        : intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.modal.submitLabel.save" });

  const modalClassName =
    activeModal === "identity-upload" || activeModal === "identity-status"
      ? "is-identity"
      : activeModal === "phone-status"
        ? "is-phone-status"
      : activeModal === "phone-activation"
        ? "is-phone-activation"
      : activeModal === "email-verification"
        ? "is-phone-activation"
        : "";
  const renderModalContent = () => {
    if (activeModal === "stats") {
      return (
        <div className="pc-modal-summary-grid">
          <ModalSummaryItem label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.stats.completedRides" })} value={statistics.completedRidesCount} />
          <ModalSummaryItem label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.stats.averageRating" })} value={statistics.averageRating} />
          <ModalSummaryItem label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.stats.loyaltyPoints" })} value={statistics.loyaltyPoints} />
          <ModalSummaryItem label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.stats.totalTime" })} value={statistics.totalRideTime} />
        </div>
      );
    }

    if (activeModal === "identity-upload") {
      const hasNewPreview = Boolean(identityPreviewUrl);
      const hasSelectedDocumentPreview = Boolean(selectedIdentityDocument && !identityPreviewUrl);
      const identityPreviewKind = hasNewPreview
        ? modalForm.identityFile?.type?.includes("pdf")
          ? "pdf"
          : modalForm.identityFile?.type?.startsWith("image/")
            ? "image"
            : "file"
        : selectedIdentityDocumentPreviewKind;
      const previewUrl = identityPreviewUrl || selectedIdentityDocumentUrl;
      const previewTitle = hasNewPreview
        ? intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.previewBeforeSend" })
        : hasSelectedDocumentPreview
          ? firstText(
              selectedIdentityDocument?.documentName,
              selectedIdentityDocument?.documentType
            ) || intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.selectedDocument" })
          : intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.noFileSelected" });

      return (
        <div className="pc-identity-upload-layout">
          <div className="pc-identity-upload-main">
            <p className="pc-identity-intro">
              {intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.intro" })}
            </p>

            <ModalSelect
              label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.selectType" })}
              name="identityType"
              value={modalForm.identityType}
              onChange={updateModalForm}
              options={effectiveIdentityTypeOptions}
            />

            <div className="pc-identity-upload-block">
              <span className="pc-identity-block-label">{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.uploadYourDocument" })}</span>
              <section
                className={`pc-identity-dropzone ${modalForm.identityFile ? "has-file" : ""}`}
                {...getIdentityRootProps({ onClick: openIdentityFilePicker })}
              >
                <input {...getIdentityInputProps()} />
                <span className="pc-identity-dropzone-icon" aria-hidden="true">
                  <UploadIcon />
                </span>
                <strong>
                  {modalForm.identityFileName
                    ? modalForm.identityFileName
                    : intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.dragDrop" })}
                </strong>
                <small>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.orClickToBrowse" })}</small>
                <em>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.acceptedFormats" })}</em>
              </section>
            </div>

            <section className="pc-identity-tips">
              <span className="pc-identity-block-label">{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.tipsTitle" })}</span>
              <ul>
                <li>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.tip1" })}</li>
                <li>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.tip2" })}</li>
                <li>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.tip3" })}</li>
              </ul>
            </section>
          </div>

          <aside className="pc-identity-preview-panel">
            <IdentityDocumentsCard
              documents={identityDocumentRows}
              emptyMessage={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityDoc.noDocumentUploadedYet" })}
              error={identityDocumentsError}
              isLoading={identityDocumentsLoading}
              onSelectDocument={handleSelectIdentityDocument}
              selectedDocumentKey={selectedIdentityDocumentKey}
              subtitle={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityDoc.typesAndStatuses" })}
              title={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityDoc.registeredDocuments" })}
            />

            <div className="pc-identity-preview-head">
              <div>
                <span className="pc-identity-block-label">{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.documentPreview" })}</span>
                <strong>{previewTitle}</strong>
                {selectedIdentityDocument && !hasNewPreview ? (
                  <div className="pc-identity-preview-badges">
                    <span className="pc-identity-preview-pill">
                      <IdentityDocumentStatusPill
                        value={selectedIdentityDocument?.status}
                        hasDocument={selectedIdentityDocument?.hasDocument}
                      />
                    </span>
                    <span className="pc-identity-preview-pill is-muted">
                      {selectedIdentityDocument?.documentType || intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.documentTypeFallback" })}
                    </span>
                  </div>
                ) : null}
              </div>
              <div className="pc-identity-preview-head-actions">
                {selectedIdentityDocumentUrl ? (
                  <button
                    type="button"
                    className="pc-identity-preview-action"
                    onClick={handleOpenSelectedIdentityDocument}
                  >
                    {intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.view" })}
                  </button>
                ) : null}
                <button type="button" className="pc-identity-preview-action" onClick={openIdentityFilePicker}>
                  {intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.edit" })}
                </button>
                {selectedIdentityDocument ? (
                  <button
                    type="button"
                    className="pc-identity-preview-action is-danger"
                    onClick={handleDeleteSelectedIdentityDocument}
                    disabled={isSaving}
                  >
                    {intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.delete" })}
                  </button>
                ) : null}
              </div>
            </div>

            <div className={`pc-identity-preview-frame ${previewUrl ? "has-preview" : "is-empty"}`}>
              {previewUrl ? (
                identityPreviewKind === "image" ? (
                  <img
                    className="pc-identity-preview-image"
                    src={previewUrl}
                    alt={
                      modalForm.identityFileName ||
                      selectedIdentityDocument?.documentName ||
                      selectedIdentityDocument?.documentType ||
                      intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.documentPreview" })
                    }
                  />
                ) : identityPreviewKind === "pdf" ? (
                  <iframe
                    className="pc-identity-preview-embed"
                    src={previewUrl}
                    title={
                      modalForm.identityFileName ||
                      selectedIdentityDocument?.documentName ||
                      selectedIdentityDocument?.documentType ||
                      intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.documentPreview" })
                    }
                  />
                ) : (
                  <div className="pc-identity-preview-empty">
                    <strong>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.previewUnavailable" })}</strong>
                    <span>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.previewUnavailableDesc" })}</span>
                  </div>
                )
              ) : (
                <div className="pc-identity-preview-empty">
                  <strong>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.noFileSelected" })}</strong>
                  <span>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.chooseFileDesc" })}</span>
                </div>
              )}
            </div>

            <div className="pc-identity-preview-meta">
              <div className="pc-identity-preview-meta-row">
                <span>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.file" })}</span>
                <strong>
                  {modalForm.identityFileName ||
                    selectedIdentityDocument?.documentName ||
                    getIdentityDocumentFileName(
                      selectedIdentityDocument?.file || selectedIdentityDocument?.documentPath
                    ) ||
                    intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.notSelected" })}
                </strong>
              </div>
              <div className="pc-identity-preview-meta-row">
                <span>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.format" })}</span>
                <strong>
                  {modalForm.identityFile
                    ? formatIdentityFileType(intl, modalForm.identityFile)
                    : selectedIdentityDocument
                      ? selectedIdentityDocumentPreviewKind === "pdf"
                        ? "PDF"
                        : selectedIdentityDocumentPreviewKind === "image"
                          ? intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fileType.image" })
                          : intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.fileFallback" })
                      : intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.notAvailable" })}
                </strong>
              </div>
              <div className="pc-identity-preview-meta-row">
                <span>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.size" })}</span>
                <strong>{modalForm.identityFile ? formatFileSize(intl, modalForm.identityFile.size) : intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.notAvailable" })}</strong>
              </div>
              <div className="pc-identity-preview-meta-row">
                <span>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.status" })}</span>
                <strong>
                  {selectedIdentityDocument
                    ? formatIdentityDocumentStatus(
                        intl,
                        selectedIdentityDocument?.status,
                        selectedIdentityDocument?.hasDocument
                      )
                    : intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.notAvailable" })}
                </strong>
              </div>
            </div>
          </aside>
        </div>
      );
    }

    if (activeModal === "identity-status") {
      const identityIsVerified = Boolean(profile.idVerified);
      const identityHasDocument = Boolean(
        profile.hasIdentityDocument ||
          modalForm.idNumber ||
          identityDocumentRows.some((document) => document.hasDocument)
      );

      return (
        <div className="pc-identity-status-panel">
          <div className={`pc-identity-status-hero ${identityIsVerified ? "is-verified" : "is-pending"}`}>
            <div className={`pc-identity-status-icon ${identityIsVerified ? "is-verified" : "is-pending"}`}>
              {identityIsVerified ? <CheckIcon /> : <ClockIcon />}
            </div>
            <span className={`pc-identity-status-badge ${identityIsVerified ? "is-verified" : "is-pending"}`}>
              {identityIsVerified
                ? intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityStatus.badgeVerified" })
                : intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityStatus.badgePending" })}
            </span>
            <strong>
              {identityIsVerified
                ? intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityStatus.heroTitleVerified" })
                : intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityStatus.heroTitlePending" })}
            </strong>
            <p>
              {identityIsVerified
                ? intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityStatus.heroDescVerified" })
                : intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityStatus.heroDescPending" })}
            </p>
          </div>

          <div className="pc-identity-details-card">
            <h4>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityStatus.detailsTitle" })}</h4>
            <div className="pc-identity-details-grid">
              <div className="pc-identity-detail-item">
                <span>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityStatus.documentType" })}</span>
                <strong>{formatIdentityType(intl, modalForm.identityType)}</strong>
              </div>
              <div className="pc-identity-detail-item">
                <span>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityStatus.number" })}</span>
                <strong>{displayValue(intl, maskIdentityNumber(modalForm.idNumber))}</strong>
              </div>
              <div className="pc-identity-detail-item">
                <span>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityStatus.statusLabel" })}</span>
                <strong>
                  {identityIsVerified
                    ? intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityStatus.validated" })
                    : identityHasDocument
                      ? intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityStatus.pending" })
                      : intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityStatus.notProvided" })}
                </strong>
              </div>
              <div className="pc-identity-detail-item">
                <span>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityStatus.submittedOn" })}</span>
                <strong>{modalForm.identitySubmittedAt ? formatDate(intl, modalForm.identitySubmittedAt) : intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.notAvailable" })}</strong>
              </div>
              <div className="pc-identity-detail-item">
                <span>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityStatus.validatedOn" })}</span>
                <strong>{modalForm.identityValidatedAt ? formatDate(intl, modalForm.identityValidatedAt) : intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityUpload.notAvailable" })}</strong>
              </div>
            </div>
          </div>

          <IdentityDocumentsCard
            documents={identityDocumentRows}
            emptyMessage={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityDoc.noDocumentUploadedYet" })}
            error={identityDocumentsError}
            isLoading={identityDocumentsLoading}
            subtitle={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityStatus.filesSubtitle" })}
            title={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityStatus.filesTitle" })}
          />

          <div className={`pc-identity-note ${identityIsVerified ? "is-verified" : "is-pending"}`}>
            <span>
              {identityIsVerified
                ? intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityStatus.noteTitleVerified" })
                : intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityStatus.noteTitlePending" })}
            </span>
            <p>
              {identityIsVerified
                ? intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityStatus.noteDescVerified" })
                : intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityStatus.noteDescPending" })}
            </p>
          </div>

          {!identityIsVerified ? (
            <button
              type="button"
              className="pc-identity-link"
              onClick={() => openProfileModal("identity-upload")}
            >
              {intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.identityStatus.editDocument" })}
            </button>
          ) : null}
        </div>
      );
    }

    if (activeModal === "phone-status") {
      const rawPhone = firstText(
        modalForm.phone,
        profileDetails?.phone,
        profileDetails?.mobile_number,
        profileDetails?.mobileNumber,
        authUser?.phone,
        authUser?.mobile_number,
        authUser?.mobileNumber
      );
      const rawCountryCode = firstText(
        modalForm.countryCode,
        profileDetails?.country_code,
        profileDetails?.countryCode,
        authUser?.country_code,
        authUser?.countryCode
      );
      const maskedPhone = maskPhoneNumber(
        intl,
        rawPhone,
        rawCountryCode
      );
      const activatedAt = formatDateTime(
        intl,
        profile.phoneVerifiedAt ||
          profile.lastActivityAt ||
          profileDetails?.phone_verified_at ||
          profileDetails?.updated_at ||
          profileDetails?.created_at
      );

      return (
        <div className="pc-phone-status-stack">
          <section className="pc-phone-status-hero">
            <div className="pc-phone-status-hero-icon" aria-hidden="true">
              <LockIcon />
              <span className="pc-phone-status-hero-badge">
                <MiniCheckIcon />
              </span>
            </div>

            <strong>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.phoneStatus.heroTitle" })}</strong>
            <p>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.phoneStatus.heroDesc" })}</p>
          </section>

          <section className="pc-phone-status-card pc-phone-status-account">
            <div className="pc-phone-status-account-icon" aria-hidden="true">
              <UserIcon />
            </div>
            <div className="pc-phone-status-account-copy">
              <span className="pc-phone-status-section-label">{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.phoneStatus.associatedAccount" })}</span>
              <strong>{maskedPhone}</strong>
            </div>
            <button
              type="button"
              className="pc-phone-status-account-action"
              onClick={() => openProfileModal("profile")}
            >
              {intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.phoneStatus.changeNumber" })}
            </button>
          </section>

          <section className="pc-phone-status-card">
            <span className="pc-phone-status-section-label">{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.phoneStatus.lastActivity" })}</span>
            <div className="pc-phone-status-details">
              <StatusDetailRow icon={CalendarIcon} label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.phoneStatus.activatedOn" })} value={activatedAt} />
              <StatusDetailRow icon={DeviceIcon} label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.phoneStatus.device" })} value={profile.deviceLabel} />
              <StatusDetailRow icon={LocationIcon} label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.phoneStatus.location" })} value={profile.activityLocation} />
              <StatusDetailRow icon={ShieldIcon} label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.phoneStatus.ipAddress" })} value={profile.ipAddress} />
            </div>
          </section>

          <section className="pc-phone-status-help">
            <ShieldIcon />
            <div>
              <strong>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.phoneStatus.helpTitle" })}</strong>
              <p>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.phoneStatus.helpDesc" })}</p>
            </div>
          </section>
        </div>
      );
    }

    if (activeModal === "phone-activation" || activeModal === "email-verification") {
      const maskedPhone = maskPhoneNumber(intl, modalForm.phone, modalForm.countryCode);
      const resendLabel =
        phoneOtpResendSecondsLeft > 0
          ? intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.otp.resendWithTimer" }, { timer: formatCountdown(phoneOtpResendSecondsLeft) })
          : intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.otp.resend" });
      const isEmailVerification = activeModal === "email-verification";

      return (
        <div className="pc-phone-activation-stack">
          <section className="pc-phone-activation-hero">
            <div className="pc-phone-activation-hero-icon" aria-hidden="true">
              <LockIcon />
              <span className="pc-phone-activation-hero-badge">
                <MiniCheckIcon />
              </span>
            </div>

            <strong>
              {isEmailVerification
                ? intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.phoneActivation.heroTitleEmail" })
                : intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.phoneActivation.heroTitlePhone" })}
            </strong>
            <p>
              {isEmailVerification ? (
                <>
                  {intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.phoneActivation.emailIntro" })}{" "}
                  <span>{modalForm.email}</span>. {intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.phoneActivation.enterCode" })}
                </>
              ) : (
                <>
                  {intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.phoneActivation.phoneIntro" })}{" "}
                  <span>{maskedPhone}</span>. {intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.phoneActivation.enterCode" })}
                </>
              )}
            </p>
          </section>

          <section className="pc-phone-activation-card">
            <span className="pc-phone-activation-section-label">
              {isEmailVerification
                ? intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.phoneActivation.codeSentToEmail" })
                : intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.phoneActivation.codeSentTo" })}
            </span>
            <div className="pc-phone-activation-row">
              <div className="pc-phone-activation-phone">
                {isEmailVerification ? <MailIcon /> : <PhoneIcon />}
                <strong>{isEmailVerification ? modalForm.email : maskedPhone}</strong>
              </div>
              <button
                type="button"
                className="pc-phone-activation-edit"
                onClick={() => openProfileModal("personal")}
              >
                {intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.phoneActivation.edit" })}
              </button>
            </div>
          </section>

          <section className="pc-phone-activation-card">
            <span className="pc-phone-activation-section-label">{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.phoneActivation.enterOtp" })}</span>
            <OtpInputGroup value={phoneOtpDigits} onChange={updatePhoneOtpDigits} />
            <div className="pc-phone-activation-meta">
              <div className="pc-phone-activation-timer">
                <ClockIcon />
                <span>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.phoneActivation.expiresIn" }, { timer: formatCountdown(phoneOtpSecondsLeft) })}</span>
              </div>
              <button
                type="button"
                className="pc-phone-activation-resend"
                onClick={resendPhoneOtp}
                disabled={phoneOtpResendSecondsLeft > 0}
              >
                {resendLabel}
              </button>
            </div>
          </section>

          <section className="pc-phone-activation-note">
            <ShieldIcon />
            <div>
              <strong>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.phoneActivation.securityTitle" })}</strong>
              <p>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.phoneActivation.securityDesc" })}</p>
            </div>
          </section>
        </div>
      );
    }

    if (activeModal === "profile") {
      return (
        <div className="pc-modal-grid">
          <ModalField label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.firstName" })} name="firstName" value={modalForm.firstName} onChange={updateModalForm} />
          <ModalField label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.lastName" })} name="lastName" value={modalForm.lastName} onChange={updateModalForm} />
          <ModalField label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.phone" })} name="phone" value={modalForm.phone} onChange={updateModalForm} />
          <ModalSelect
            label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.dialCode" })}
            name="countryCode"
            value={modalForm.countryCode}
            onChange={updateModalForm}
            options={countryCodeOptions}
          />
          <ModalField label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.email" })} name="email" type="email" value={modalForm.email} onChange={updateModalForm} />
          
        </div>
      );
    }

    if (activeModal === "personal" || activeModal === "security") {
      return (
        <div className="pc-modal-grid">
          <ModalField label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.firstName" })} name="firstName" value={modalForm.firstName} onChange={updateModalForm} />
          <ModalField label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.lastName" })} name="lastName" value={modalForm.lastName} onChange={updateModalForm} />
          <ModalField label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.phone" })} name="phone" value={modalForm.phone} onChange={updateModalForm} />
          <ModalSelect
            label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.dialCode" })}
            name="countryCode"
            value={modalForm.countryCode}
            onChange={updateModalForm}
            options={countryCodeOptions}
          />
          <ModalField label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.email" })} name="email" type="email" value={modalForm.email} onChange={updateModalForm} />
          <ModalField label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.country" })} name="country" value={modalForm.country} onChange={updateModalForm} />
        </div>
      );
    }

    if (activeModal === "loyalty" || activeModal === "account") {
      return (
        <div className="pc-modal-grid">
          <ModalField label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.clientType" })} name="userType" value={modalForm.userType} onChange={updateModalForm} />
          <ModalSelect
            label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.status" })}
            name="accountStatus"
            value={modalForm.accountStatus}
            onChange={updateModalForm}
            options={accountStatusOptions}
          />
          <ModalSelect
            label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.preferredMethod" })}
            name="paymentMethod"
            value={modalForm.paymentMethod}
            onChange={updateModalForm}
            options={paymentMethodOptions}
          />
        </div>
      );
    }

    if (activeModal === "payment") {
      return (
        <div className="pc-modal-grid">
          <ModalSelect
            label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.preferredMethod" })}
            name="paymentMethod"
            value={modalForm.paymentMethod}
            onChange={updateModalForm}
            options={paymentMethodOptions}
          />
          <ModalField label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.cardHolder" })} name="cardHolder" value={modalForm.cardHolder} onChange={updateModalForm} />
          <ModalField label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.cardBrand" })} name="cardBrand" value={modalForm.cardBrand} onChange={updateModalForm} />
          <ModalField label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.cardLastDigits" })} name="cardLastDigits" value={modalForm.cardLastDigits} onChange={updateModalForm} />
        </div>
      );
    }

    if (activeModal === "addresses") {
      return (
        <div className="pc-modal-grid">
          <ModalField label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.addressLabel" })} name="addressLabel" value={modalForm.addressLabel} onChange={updateModalForm} />
          <ModalField label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.address" })} name="address" value={modalForm.address} onChange={updateModalForm} />
          <ModalField label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.city" })} name="city" value={modalForm.city} onChange={updateModalForm} />
          <ModalField label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.state" })} name="state" value={modalForm.state} onChange={updateModalForm} />
          <ModalField label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.country" })} name="country" value={modalForm.country} onChange={updateModalForm} />
        </div>
      );
    }

    if (activeModal === "wallet") {
      return (
        <div className="pc-modal-grid">
          <ModalSummaryItem label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.currentBalance" })} value={profile.walletBalance} />
          <ModalSelect
            label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.preferredMethod" })}
            name="paymentMethod"
            value={modalForm.paymentMethod}
            onChange={updateModalForm}
            options={paymentMethodOptions}
          />
        </div>
      );
    }

    if (activeModal === "support") {
      return (
        <div className="pc-modal-grid">
          <ModalField label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.subject" })} name="supportSubject" value={modalForm.supportSubject} onChange={updateModalForm} />
          <ModalTextarea
            label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.message" })}
            name="supportMessage"
            value={modalForm.supportMessage}
            onChange={updateModalForm}
            placeholder={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.fields.supportPlaceholder" })}
          />
        </div>
      );
    }

    return null;
  };
  return (
    <>
      <div className="pc-hero">
        <h1>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.hero.title" })}</h1>
        <p>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.hero.subtitle" })}</p>
      </div>

      {isLoading ? (
        <div className="pc-state-card">{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.loadingProfile" })}</div>
      ) : null}

      {errorMessage ? (
        <div className="pc-state-card is-error">{errorMessage}</div>
      ) : null}

      <div className="pc-layout">
        <section className="pc-main">
          <article className="pc-card pc-profile-card">
            <div className="pc-profile-top">
              <div className="pc-profile-avatar-wrap">
                <div className="pc-profile-avatar" aria-hidden="true">
                  <img src={profile.avatar} alt="" aria-hidden="true" />
                </div>
                <input
                  ref={profileAvatarInputRef}
                  type="file"
                  accept="image/*"
                  className="pc-avatar-file-input"
                  onChange={handleProfileAvatarChange}
                  aria-hidden="true"
                  tabIndex={-1}
                />
                <button
                  type="button"
                  className="pc-avatar-camera"
                  aria-label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.editPhoto" })}
                  onClick={handleOpenProfileAvatarPicker}
                  disabled={isAvatarUploading}
                >
                  <CameraIcon />
                </button>
              </div>

              <div className="pc-profile-copy">
                <div className="pc-profile-name-row">
                  <h2>{profile.displayName}</h2>
                  <button type="button" className="pc-edit-btn" onClick={() => openProfileModal("profile")}>
                    {intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.editProfile" })}
                  </button>
                </div>
                <div className="pc-profile-contact">
                  <span className="pc-phone">
                    {profile.phone}
                    {profile.phoneVerified ? (
                      <span className="pc-verified-pill">
                        {intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.common.verified" })} <CheckIcon />
                      </span>
                    ) : null}
                  </span>
                  <span className="pc-email">{profile.email}</span>
                </div>
              </div>
            </div>

            <div className="pc-profile-meta">
              <div className="pc-profile-meta-item">
                <span>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.memberSince" })}</span>
                <strong>{profile.createdAt}</strong>
              </div>
              <div className="pc-profile-meta-item">
                <span>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.primaryPaymentMethod" })}</span>
                <strong>{profile.paymentMethod}</strong>
              </div>
              <div className="pc-profile-meta-item">
                <span>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.status" })}</span>
                <strong><i className="pc-status-pill">{profile.status}</i></strong>
              </div>
            </div>
          </article>

          <article className="pc-card">
            <div className="pc-card-head">
              <h3>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.card.myStats" })}</h3>
              <HeadAction onClick={() => openProfileModal("stats")}>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.view" })}</HeadAction>
            </div>
            <div className="pc-stats">
              <StatItem icon={CarIcon} value={statistics.completedRidesCount} label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.stats.completedRides" })} tone="green" onClick={() => openProfileModal("stats")} />
              <StatItem icon={StarIcon} value={statistics.averageRating} label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.stats.averageRating" })} tone="violet" onClick={() => openProfileModal("stats")} />
              <StatItem icon={svgTrophy} value={statistics.loyaltyPoints} label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.stats.loyaltyPoints" })} tone="orange" onClick={() => openProfileModal("stats")} />
              <StatItem icon={ClockIcon} value={statistics.totalRideTime} label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.stats.totalTime" })} tone="blue" onClick={() => openProfileModal("stats")} />
            </div>
          </article>

          <article className="pc-card">
            <div className="pc-card-head">
              <h3>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.card.personalInfo" })}</h3>
              <HeadAction onClick={() => openProfileModal("personal")}>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.edit" })}</HeadAction>
            </div>
            <div className="pc-info-list">
              <InfoRow icon={UserIcon} label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.info.fullName" })} value={profile.displayName} onClick={() => openProfileModal("personal")} />
              <InfoRow
                icon={PhoneIcon}
                label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.info.phoneNumber" })}
                value={profile.phone}
                verified={false}
                actionLabel={profile.phoneVerified ? intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.common.active" }) : intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.common.activate" })}
                actionIcon={profile.phoneVerified ? CheckIcon : LockIcon}
                actionClassName={profile.phoneVerified ? "is-active" : "is-pending"}
                onClick={() =>
                  profile.phoneVerified ? openPhoneStatusModal() : openPhoneActivationModal()
                }
              />
              <InfoRow
                icon={MailIcon}
                label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.info.emailAddress" })}
                value={profile.email}
                verified={profile.emailVerified}
                actionLabel={profile.emailVerified ? intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.common.verified" }) : intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.common.toVerify" })}
                actionIcon={profile.emailVerified ? CheckIcon : LockIcon}
                actionClassName={profile.emailVerified ? "is-active" : "is-pending"}
                onClick={profile.emailVerified ? undefined : openEmailVerificationModal}
              />
              <InfoRow icon={LocationIcon} label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.info.country" })} value={profile.address} onClick={() => openProfileModal("addresses")} />
              <InfoRow
                icon={IdIcon}
                label={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.info.identityDocument" })}
                value={profile.identityStatusLabel}
                verified={profile.idVerified}
                onClick={openIdentityModal}
              />
            </div>
          </article>
        </section>

        <aside className="pc-sidebar">
          <article className="pc-side-card pc-loyalty-card">
            <div className="pc-card-head">
              <h3>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.card.loyalty" })}</h3>
             {/*  <HeadAction onClick={() => openProfileModal("loyalty")}>Modifier</HeadAction> */}
            </div>
            <div className="pc-loyalty-top">
              <div className="pc-loyalty-medal" aria-hidden="true">
                <img src={loyaltyBadge} alt="" aria-hidden="true" />
              </div>
              <div className="pc-loyalty-copy">
                <strong>{profile.userType}</strong>
                <span>{profile.status}</span>
              </div>
            </div>

            <div className="pc-progress-block">
              <div className="pc-progress-track">
                <div className="pc-progress-bar" style={{ width: `${profile.progressPercent}%` }} />
              </div>
              <strong>{profile.walletBalance}</strong>
            </div>

            <button type="button" className="pc-tier-btn" onClick={() => openProfileModal("loyalty")}>
              <span>{profile.paymentMethod}</span>
              <small>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.preferredMethod" })}</small>
              <ChevronIcon />
            </button>
          </article>

          <article className="pc-side-card">
            <div className="pc-card-head">
              <h3>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.card.preferences" })}</h3>
              <HeadAction onClick={() => openProfileModal("payment")}>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.add" })}</HeadAction>
            </div>
            <div className="pc-right-list">
              <RightItem
                icon={CardIcon}
                title={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.card.paymentMethods" })}
                desc={`${pluralize(
                  profile.paymentCardCount,
                  intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.pluralize.registeredCard" }),
                  intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.pluralize.registeredCards" })
                )} - ${profile.paymentMethod}`}
                onClick={() => openProfileModal("payment")}
              />
              <RightItem
                icon={LocationIcon}
                title={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.card.savedAddresses" })}
                desc={pluralize(
                  profile.favAddressCount,
                  intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.pluralize.address" }),
                  intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.pluralize.addresses" })
                )}
                onClick={() => openProfileModal("addresses")}
              />
              <RightItem icon={CarIcon} title={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.card.walletBalance" })} desc={profile.walletBalance}  />
            </div>
          </article>

          <article className="pc-side-card">
            <div className="pc-card-head">
              <h3>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.card.quickActions" })}</h3>
            </div>
            <div className="pc-right-list">
              <RightItem icon={SettingsIcon} title={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.card.accountSettings" })} desc={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.card.accountSettingsDesc" })}  />
              <RightItem icon={ShieldIcon} title={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.card.security" })} desc={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.card.securityDesc" })}  />
              <RightItem icon={HelpIcon} title={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.card.helpSupport" })} desc={intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.card.helpSupportDesc" })}  />
            </div>

            <button type="button" className="pc-logout-btn">
              <LogoutIcon />
              <span>{intl.formatMessage({ id: "dashboardEntreprise.profileEntreprise.logout" })}</span>
            </button>
          </article>
        </aside>
      </div>

      <ProfileModal
        open={Boolean(activeModal)}
        title={modalTitle}
        feedback={modalFeedback}
        className={modalClassName}
        isSaving={isSaving}
        submitLabel={modalSubmitLabel}
        onClose={closeProfileModal}
        onSubmit={handleSaveModal}
      >
        {renderModalContent()}
      </ProfileModal>
    </>
  );
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5v5l3 2" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="8" r="3" />
      <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M10.8 4.8h2.4l.4 2a6.2 6.2 0 0 1 1.6.7l1.8-1 1.7 1.7-1 1.8c.3.5.5 1 .7 1.6l2 .4v2.4l-2 .4a6.2 6.2 0 0 1-.7 1.6l1 1.8-1.7 1.7-1.8-1a6.2 6.2 0 0 1-1.6.7l-.4 2h-2.4l-.4-2a6.2 6.2 0 0 1-1.6-.7l-1.8 1-1.7-1.7 1-1.8a6.2 6.2 0 0 1-.7-1.6l-2-.4v-2.4l2-.4c.1-.6.4-1.1.7-1.6l-1-1.8 1.7-1.7 1.8 1c.5-.3 1-.5 1.6-.7l.4-2Z" />
      <circle cx="12" cy="12" r="2.4" />
    </svg>
  );
}

function HelpIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 15.5v.3M9.8 9.8a2.5 2.5 0 1 1 4.4 1.7c-.9.8-1.8 1.2-1.8 2.5" />
    </svg>
  );
}

function svgTrophy() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 5h10v2a5 5 0 0 1-10 0V5Z" />
      <path d="M7 7H5a2 2 0 0 0 2 3M17 7h2a2 2 0 0 1-2 3" />
      <path d="M10 15h4M9 19h6" />
      <path d="M12 9.5v5" />
    </svg>
  );
}
